import React, { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { Space } from 'antd';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import Flexbox from '@common/components/Flexbox';
import AccountDropdown from '@features/navigation/components/AccountDropdown';
import EnhancedDropdown from '@features/navigation/components/EnhancedDropdown/EnhancedDropdown';
import { AccountItem } from '@features/navigation/hooks/interfaces';
import { useAccountContextProvider } from '@features/navigation/hooks/useAccountContextProvider';

const AccountSelection: React.FC = (): ReactElement => {
  const location = useLocation();
  const pathname = location?.pathname?.slice(1);
  const {
    accounts,
    selectedAccounts,
    accountsLoading,
    agencies,
    selectedAgencies,
    agenciesLoading,
    advertisers,
    selectedAdvertisers,
    advertisersLoading,
    setAccounts,
    setAdvertisers,
    setAgencies,
  } = useAccountContextProvider();

  const showOtherDropdowns = pathname !== 'inventory' && pathname !== 'data-audience';

  const handleApply =
    (type: 'agency' | 'advertiser') =>
    (items: MenuItemType[]): void => {
      const mappedItems = items.map((item) => ({ label: item.label.toString(), key: item.key }));

      if (type === 'agency') {
        setAgencies(mappedItems);
      }

      if (type === 'advertiser') {
        setAdvertisers(mappedItems);
      }
    };

  const handleChangeAccount = (account: AccountItem): void => {
    setAccounts([account]);
  };

  return (
    <Space size={24}>
      <Flexbox flexDirection='column' style={{ maxWidth: 120 }}>
        <AccountDropdown
          loading={accountsLoading}
          selectedAccounts={selectedAccounts}
          accounts={accounts}
          onChangeAccount={handleChangeAccount}
        />
      </Flexbox>
      {showOtherDropdowns && (
        <>
          <Flexbox flexDirection='column' style={{ maxWidth: 120 }}>
            <EnhancedDropdown
              previewSelected
              title='Agency'
              headerText='Agencies'
              dataTestId='agency-dropdown'
              items={agencies}
              defaultSelected={selectedAgencies}
              loading={agenciesLoading}
              onApply={handleApply('agency')}
            />
          </Flexbox>
          <Flexbox flexDirection='column' style={{ maxWidth: 120 }}>
            <EnhancedDropdown
              previewSelected
              title='Advertiser'
              headerText='Advertisers'
              dataTestId='advertiser-dropdown'
              items={advertisers}
              defaultSelected={selectedAdvertisers}
              loading={advertisersLoading}
              onApply={handleApply('advertiser')}
            />
          </Flexbox>
        </>
      )}
    </Space>
  );
};

export default AccountSelection;
